/**
 * Abertura do microfone e medição de nível — comuns aos dois ouvintes.
 */

/**
 * As três constraints não são enfeite. `echoCancellation` é a primeira linha
 * de defesa contra a fala do Will voltando pelo microfone (a guarda de eco em
 * `turno.ts` é a segunda, para quando esta falha); `noiseSuppression` reduz o
 * ruído de fundo que faz o VAD abrir turno sozinho; `autoGainControl` mantém
 * o nível utilizável quando a pessoa se afasta do aparelho.
 */
export async function abrirMicrofone(): Promise<MediaStream> {
  const md = navigator.mediaDevices as MediaDevices | undefined;
  if (!md || typeof md.getUserMedia !== 'function') {
    throw new Error('Este navegador não dá acesso ao microfone.');
  }
  return md.getUserMedia({
    audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    video: false,
  });
}

export interface MedidorDeNivel {
  parar(): void;
}

/**
 * Nível de entrada em 0..1 para a animação do domo.
 *
 * RMS no domínio do tempo em vez de pico: o pico salta com qualquer estalo e
 * faz o domo tremer. A suavização exponencial (sobe rápido, desce devagar)
 * existe pelo mesmo motivo — a fala tem silêncios entre sílabas, e sem ela a
 * animação pisca a cada consoante.
 */
export function criarMedidorDeNivel(
  stream: MediaStream,
  onLevel: (level: number) => void,
): MedidorDeNivel {
  const ctx = new AudioContext();
  const fonte = ctx.createMediaStreamSource(stream);
  const analisador = ctx.createAnalyser();
  analisador.fftSize = 1024;
  analisador.smoothingTimeConstant = 0.6;
  fonte.connect(analisador);

  const amostras = new Uint8Array(analisador.fftSize);
  let suavizado = 0;
  let quadro = 0;
  let vivo = true;

  const passo = (): void => {
    if (!vivo) return;
    analisador.getByteTimeDomainData(amostras);
    let soma = 0;
    for (let i = 0; i < amostras.length; i++) {
      const desvio = (amostras[i] - 128) / 128;
      soma += desvio * desvio;
    }
    // ×4 aproxima a faixa útil da fala normal de 0..1 sem estourar em grito.
    const rms = Math.min(1, Math.sqrt(soma / amostras.length) * 4);
    suavizado = rms > suavizado ? rms : suavizado * 0.85 + rms * 0.15;
    onLevel(suavizado);
    quadro = requestAnimationFrame(passo);
  };
  quadro = requestAnimationFrame(passo);

  return {
    parar(): void {
      vivo = false;
      cancelAnimationFrame(quadro);
      fonte.disconnect();
      analisador.disconnect();
      // O AudioContext segura hardware de áudio; sem close() o indicador de
      // microfone do navegador continua aceso depois do stop().
      void ctx.close().catch(() => undefined);
      onLevel(0);
    },
  };
}

/** Encerra as trilhas — é o que apaga o indicador de microfone da aba. */
export function fecharStream(stream: MediaStream | null): void {
  if (!stream) return;
  for (const t of stream.getTracks()) t.stop();
}
